'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {parse} from 'qs';

import {FaInfoCircle, FaBook, FaExclamationTriangle, FaChevronRight, FaChevronDown} from "react-icons/fa";

import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Accordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {Campuses} from "@uw-it-sis/lib-js-common/lib/Constants";
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import HelpPopover from '@uw-it-sis/lib-react/lib/HelpPopover';
import {isEmpty, pluralize} from '@uw-it-sis/lib-react/lib/Utils';
import Lists from '@uw-it-sis/lib-react/lib/Lists';
import Notify from '@uw-it-sis/lib-react-myplan/lib/Notify';
import {withRouter} from "@uw-it-sis/lib-react/lib/WithRouter";

import SectionCode from '@uw-it-sis/lib-react-myplan/lib/SectionCode';
import AddOrRemoveSectionButton from '@uw-it-sis/lib-react-myplan/lib/AddOrRemoveSectionButton';
import SectionMeetingDetails from '@uw-it-sis/lib-react-myplan/lib/SectionMeetingDetails';

import CourseSyllabus from './CourseSyllabus';
import {createTextbookParamFromActivity} from '../lib/Utils';

/**
 * A row in the section table.
 */
class SectionRow extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.state = {
            // If the isExpanded is null, then default all primary to be open, else use the isExpanded value
            isExpanded: ( this.props.isExpanded === null ? this.props.section.primary : this.props.isExpanded )
        };
    }

    componentDidUpdate(prevProps) {
        // If the parent table expand has been toggled, then update the current row state
        if (this.props.isExpanded !== prevProps.isExpanded) {
            this.setState({
                isExpanded: this.props.isExpanded
            });
        }
    }

    toggleAdditionalDetails = () => this.setState({isExpanded: !this.state.isExpanded});

    render() {
        const section = this.props.section;
        const course = this.props.course;
        const mode = this.props.section.onlineLearningText ? this.props.section.onlineLearningText : "In-person";

        const sectionUniqueId = `${TermUtils.makeTermName(section.atpId).toLowerCase()}-${section.code.toLowerCase()}`;

        let colspan = 8;

        let code = <SectionCode section={section} additionalAnalytics={{context: 'CourseDetails'}} />;

        let termCode = TermUtils.makeTermName(section.atpId);

        /**
         *  Type (Credit)
         *  Show credits for section if credits is not empty and its a primary section
         */
        let typeCredits = "";
        if (section.credits !== '' && section.primary) {
            typeCredits = <>
                ({section.credits}<span className="sr-only"> {pluralize('credit', section.credits)}</span>)
            </>;
        }
        let type = <span className="text-capitalize">{section.activityOfferingType} {typeCredits}</span>;

        /**
         *  Instructor
         */
        let instructor = null;
        // Build array of all instructors names without duplicates
        let allInstructors = section.allInstructors.map(instructor => instructor.name).filter((v, i, s) => s.indexOf(v) === i);

        if ( allInstructors && allInstructors.length ) {
            let useAccordion = allInstructors.length > 1;

            if ( useAccordion ) {
                const InstructorsToggle = ({children, eventKey}) => {
                    const decoratedOnClick = useAccordionButton(eventKey, () => {});

                    return (
                        <Button variant="accordion-truncate"
                                onClick={decoratedOnClick}/>
                    )

                };

                instructor =
                    <Accordion className="accordion-truncate mb-1">
                        <Accordion.Collapse eventKey={"instructors-list-" + sectionUniqueId}>
                            <Lists data={allInstructors} className="mb-1" unstyled />
                        </Accordion.Collapse>
                        <InstructorsToggle eventKey={"instructors-list-" + sectionUniqueId} />
                    </Accordion>;
            } else {
                instructor =
                    <div className="mb-1">
                        {allInstructors[0]}
                    </div>;
            }
        }

        /**
         * Instructor/Syllabus cell
         */
        let syllabus = null;
        if (section.hasSyllabus && appStore.isAuthenticated()) {
            syllabus = <CourseSyllabus section={section}/>;
        }

        let instructorSyllabusCell =
            <>
                {instructor}
                {syllabus}
            </>;

        /**
         *  SLN
         */
        let sln;
        // If user is a UW user, show link to time schedule section data
        if (appStore.isAuthenticated() && ! appStore.isSocialUser()) {
            sln =
                <a href={'https://sdb.admin.washington.edu/timeschd/uwnetid/sln.asp?QTRYR=' + section.qtryr + '&SLN=' + section.registrationCode} target="_blank">
                    {section.registrationCode}
                </a>;
        } else {
            sln = section.registrationCode;
        }

        /**
         * Est ENRL Status
         */
        let state = section.stateKey.toLowerCase();

        let estEnrlStatusCssClasses = classnames(
            {'text-success': ( state === 'active' && section.enrollStatus === 'open' )},
            {'text-warning': ( state !== 'active' )}
        );

        let iconElement = null;
        let enroll;
        let status = "--";
        let count = null;
        if (section.enrollStatus) {
            switch ( section.enrollStatus.toLowerCase() ) {
                case 'open':
                case 'closed':
                    status = section.enrollStatus;
                    break;
                default:
                    status = "--";
            }
        }

        if ( state === 'active' ) {
            // Don't show negative numbers for available seats since sometimes counts are larger than maximums
            let availableSeats = Math.max(0, (section.enrollMaximum - section.enrollCount));

            iconElement =
                <HelpPopover title="Section Status and Enrollment" icon={FaInfoCircle} placement="top" className="ms-2">
                    <p>{availableSeats} seats available out of {section.enrollEstimate === 'estimate' ? 'an estimated ' :''}{section.enrollMaximum}.</p>
                    <p>Updated a few minutes ago.</p>
                    {( appStore.isAuthenticated() && ! appStore.isSocialUser() ) ? <p>Click the SLN for up-to-date seat availability.</p> : null}
                </HelpPopover>;
            count =
                <small className="d-block">
                    {availableSeats} <span className="sr-only">seats available</span>
                    <abbr title="available" aria-hidden={true}>avail</abbr> of {section.enrollMaximum}{section.enrollEstimate === 'estimate' ? <abbr title="estimated">E</abbr> : ''}
                </small>;

            enroll =
                <>
                    <span className={estEnrlStatusCssClasses}>{status}</span>
                    {iconElement}
                </>;
        } else {
            enroll =
                <span className={estEnrlStatusCssClasses}>
                    <FaExclamationTriangle className="me-2" aria-hidden={true} />
                    {state}
                </span>;
        }

        let estEnrlStatus =
            <>
                <div className="d-flex align-items-center fw-bold text-capitalize lead">{enroll}</div>
                {count}
            </>;

        /**
         *  Textbook
         */
        let textbook, textbookLink;
        if (section.campus.toLowerCase() === Campuses.tacoma) {
            let subjectAreaToken = '';
            if (course.subjectArea) {
                /*
                  Per the change in Follet provider for Tacoma's bookstore described in PLANNING-1728,
                  replace spaces with dashes in the subject area.

                  No Tacoma subject ares have an & at time of writing.  If one is introduced, the code would need to be
                  updated to use the encoding Follet would require.
                 */
                subjectAreaToken = course.subjectArea.replaceAll(' ', '-');
            }
            textbookLink = `https://www.bkstr.com/webApp/discoverView?bookstore_id-1=2335&div-1=&term_id-1=${section.atpId}&dept-1=${subjectAreaToken}&course-1=${course.courseNumber}&section-1=${section.code}`;
        } else {
            let textbookParam = createTextbookParamFromActivity(section);
            if (textbookParam) {
                textbookLink = `https://ubookstore.com/pages/adoption-search/course=${textbookParam}`;
            }
        }

        if (textbookLink) {
            textbook =
                <Button variant="icon-link"
                        size="lg"
                        className="px-1"
                        href={textbookLink}
                        target="_blank"
                        title="Display Textbook Information">
                    <FaBook aria-hidden={true} />
                </Button>;
        }

        /**
         *  Notify
         *  If section is withdrawn or add code required or independent study, then dont allow notify action
         */
        let notify = null;
        if (! appStore.isAdviser() && appStore.isAuthenticated() && ! appStore.isSocialUser()) {
            colspan++;
            notify =
                <td>
                    <Notify course={course} section={section} size="lg" className="px-1" additionalAnalytics={{context: 'CourseDetails'}}/>
                </td>;
        }

        /**
         *  Add or Remove from plan
         */
        let planAction = null;
        if (! appStore.isAdviser() && appStore.isAuthenticated()) {
            colspan++;
            planAction =
                <td>
                    <AddOrRemoveSectionButton courseCode={course.courseCode} section={section} size="lg" additionalAnalytics={{context: 'CourseDetails'}} />
                </td>;
        }

        /**
         * Block registration courses
         */
        let blockRegistrationRow = null;
        if (! isEmpty(section.blockRegistration)) {
            let blockRegText;
            let blockCoursesList =
                <ul className="comma-separated">
                    {
                        section.blockRegistration.courses.filter(blockCourse => !blockCourse.parent).map((blockCourse, i) => {
                            return <li key={i}>{blockCourse.label}</li>;
                        })
                    }
                </ul>;
            // Section is a block registration parent
            if (section.blockRegistration.parent) {
                blockRegText =
                    <div className="course-sections-blockreg">
                        <span>Includes:&nbsp;</span>
                        <span>{blockCoursesList}</span>
                    </div>;
            } else {
                // Section is a block registration child
                blockRegText =
                    <div className="course-sections-blockreg">
                        Some seats reserved for cluster registration.
                    </div>;
            }

            blockRegistrationRow =
                <tr>
                    <td className="pt-0">&nbsp;</td>
                    <td className="pt-0" colSpan={colspan - 1}>
                        {blockRegText}
                    </td>
                </tr>;
        }

        /**
         *  Additional Details
         */
        let additionalDetailsRow = null;
        /**
         * This is kind of messy, but this is replicating the logic of the old KRAD display logic for "Additional Details"
         */
        let additionalDetails = [];
        // if summer term data is present, display the summer term value
        if (section.summerTerm && section.summerTerm !== '')  additionalDetails.push(section.summerTerm);
        // if geneds list has items, display a comma separated string of values
        if (section.geneds.length) additionalDetails.push('General Edu Req: ' + section.geneds.sort().join(", "));
        // if gradingOption is not empty, display the value
        if (section.gradingOption && section.gradingOption !== '') additionalDetails.push(section.gradingOption);
        // if onlineLearning is not empty, display the value
        if (section.onlineLearning && section.onlineLearning !== '') additionalDetails.push(section.onlineLearning);
        // if honorsSection is true, display message
        if (section.honorsSection) additionalDetails.push("Honors");
        // if jointOffering is true, display message
        if (section.jointOffering) additionalDetails.push("Jointly offered");
        // if research is true, display message
        if (section.research) additionalDetails.push("Research");
        // if serviceLearning is true, display message
        if (section.serviceLearning) additionalDetails.push("Service learning");
        // if newThisYear is true, display message
        if (section.newThisYear) additionalDetails.push("New course");
        // if ineligibleForFinancialAid is true, display message
        if (section.ineligibleForFinancialAid) additionalDetails.push("May not be eligible for Financial Aid");
        // if course fee set, display the value
        if (section.feeAmount && section.feeAmount !== '') additionalDetails.push("Course Fee $" + section.feeAmount);
        // if there are any section comments, display them
        if (section.sectionComments && section.sectionComments !== '') additionalDetails.push(section.sectionComments);

        // Add 'view more details link' if UW user to the end of additional details
        if (appStore.isAuthenticated() && ! appStore.isSocialUser()) {
            additionalDetails.push(
                <a href={'https://sdb.admin.washington.edu/timeschd/uwnetid/sln.asp?QTRYR=' + section.qtryr + '&SLN=' + section.registrationCode} target="_blank">
                    View more details
                </a>
            );
        }

        let toggle = null;
        if (additionalDetails.length) {
            additionalDetailsRow =
                <tr hidden={!this.state.isExpanded}>
                    <td className="pt-0">&nbsp;</td>
                    <td className="pt-0" colSpan={colspan - 1}>
                        <strong>Additional Details</strong>
                        <Lists data={additionalDetails} unstyled className="mb-0" />
                    </td>
                </tr>;

            const ArrowIcon = (this.state.isExpanded ? FaChevronDown : FaChevronRight);
            let title = (this.state.isExpanded ? 'Hide' : 'View' ) + " Additional Details";

            toggle =
                <Button variant="toggle"
                        className="p-0 text-link-color"
                        style={{width: '30px', height: "30px"}}
                        title={title}
                        onClick={this.toggleAdditionalDetails}>
                    <ArrowIcon aria-hidden={true} />
                </Button>;
        }

        // If the section is planned then set the style.
        let sectionStateClasses = classnames(
            {'text-muted': state !== 'active'},
            {'bg-planned': section.planItemId !== null}
        );

        return (
            <tbody id={sectionUniqueId} className={sectionStateClasses}>
                <tr>
                    <td className="p-1">{toggle}</td>
                    <td>{code}</td>
                    <td>{type}</td>
                    <td><SectionMeetingDetails meetings={section.meetingDetailsList} mode={mode} campus={section.campus} /></td>
                    <td>{instructorSyllabusCell}</td>
                    <td><span className="sr-only">Schedule Line Number: </span>{sln}</td>
                    <td>{estEnrlStatus}</td>
                    <td>{textbook}</td>
                    {notify}
                    {planAction}
                </tr>
                {blockRegistrationRow}
                {additionalDetailsRow}
            </tbody>
        );
    }
}

SectionRow.propTypes = {
    course: PropTypes.object.isRequired,
    section: PropTypes.object.isRequired,
    isExpanded: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.bool])
};

/**
 * The CDP section data table.
 */
class SectionsTable extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.state = {
            // if all sections are primaries (default open) then set the open all toggle to true, else set to null for initial value
            isTableExpanded: (this.props.sections.every(section => section.primary) ? true : null)
        };
    }

    componentDidMount() {
        let section = parse(this.props.location.search, { ignoreQueryPrefix: true }).section;
        if (section) {
            let sectionItem = document.getElementById(section.toLowerCase());
            if (sectionItem) {
                sectionItem.scrollIntoView(true);
            }
        }
    }

    toggleTableAdditionalDetails = () => {
        let newState = {
            isTableExpanded: ( this.state.isTableExpanded === null ? true : !this.state.isTableExpanded )
        }
        this.setState(newState);
    }

    render() {
        let sectionNotifyHeader, sectionAddToPlanHeader, sectionNotifyCol, sectionAddToPlanCol = null;

        if (! appStore.isAdviser() && appStore.isAuthenticated() && ! appStore.isSocialUser()) {
            sectionNotifyHeader = <th className="border-end-0"><span className="sr-only">Notify</span><span aria-hidden={true}>&nbsp;</span></th>;
            sectionNotifyCol = <col style={{width: '50px', minWidth: '50px'}}/>;
        }

        if (! appStore.isAdviser() && appStore.isAuthenticated()) {
            sectionAddToPlanHeader = <th className="border-end-0"><span className="sr-only">Add or Remove from Plan</span><span aria-hidden={true}>&nbsp;</span></th>;
            sectionAddToPlanCol = <col style={{width: '50px', minWidth: '50px'}}/>;
        }

        let course = this.props.course;
        let sections = this.props.sections;

        let tableBody = sections.map( (section, i) => <SectionRow course={course} section={section} key={i} isExpanded={this.state.isTableExpanded} /> );

        let toggle = <span>&nbsp;</span>;
        if (sections.length > 1) {
            const ArrowIcon = (this.state.isTableExpanded ? FaChevronDown : FaChevronRight);
            let title = (this.state.isTableExpanded ? 'Collapse' : 'Expand' ) + " All Section Details";
            toggle =
                <Button variant="toggle"
                        className="p-0 text-link-color"
                        title={title}
                        style={{width: '30px', height: "30px"}}
                        onClick={this.toggleTableAdditionalDetails}>
                    <ArrowIcon aria-hidden={true} />
                </Button>;
        }

        // marginTop trick - there is an issue where the table-responsive class causes an issue with popovers
        // within a table which positions them incorrectly if the popover would go outside the table container
        // Setting a negative margin and then added the same amount of margin to the child will position everything
        // in the correct location, but now the popover has space from the negative margin to position correctly.
        return (
            <div className="cdpSectionsTable">
                <div className="table-responsive">
                    <Table borderless className="mb-0">
                        <caption className="sr-only">{`List of sections for ${this.props.course.courseCode} being offered in the ${this.props.termName} quarter`}</caption>
                        <colgroup>
                            <col style={{width: '37px', minWidth: '37px'}}/>
                            <col style={{width: '8%', minWidth: '8%'}}/>
                            <col style={{width: '13%', minWidth: '13%'}}/>
                            <col style={{width: '35%', minWidth: '35%'}}/>
                            <col style={{width: '17%', minWidth: '17%'}}/>
                            <col style={{width: '7%', minWidth: '7%'}}/>
                            <col style={{width: '12%', minWidth: '12%'}}/>
                            <col style={{width: '50px', minWidth: '50px'}}/>
                            {sectionNotifyCol}
                            {sectionAddToPlanCol}
                        </colgroup>
                        <thead>
                            <tr className="fs-8">
                                <th className="p-1">{toggle}</th>
                                <th>Section</th>
                                <th>Type (Credit)</th>
                                <th>Meeting Time &amp; Location</th>
                                <th>Instructor</th>
                                <th><abbr title="Schedule Line Number">SLN</abbr></th>
                                <th>
                                    <span className="sr-only">Estimated Enrollment Status</span>
                                    <span aria-hidden={true}>Est ENRL Status</span>
                                </th>
                                <th className="border-end-0"><span className="sr-only">Textbook</span></th>
                                {sectionNotifyHeader}
                                {sectionAddToPlanHeader}
                            </tr>
                        </thead>
                        {tableBody}
                    </Table>
                </div>
            </div>
        );
    }
}

SectionsTable.propTypes = {
    course: PropTypes.object.isRequired,
    sections: PropTypes.array.isRequired,
    termName: PropTypes.string.isRequired
};

export default withRouter(SectionsTable);
